import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AppRole } from '@/lib/permissions';

export interface RoleRequest {
  id: string;
  user_id: string;
  requested_role: AppRole;
  current_role: AppRole | null;
  status: 'pending' | 'approved' | 'rejected';
  justification: string | null;
  character_type: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
}

export const useRoleRequest = () => {
  const [pendingRequests, setPendingRequests] = useState<RoleRequest[]>([]);
  const [myRequest, setMyRequest] = useState<RoleRequest | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPendingRequests = async () => {
    try {
      // Fetch role requests
      const { data: requestsData, error: requestsError } = await supabase
        .from('role_requests' as any)
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;

      if (!requestsData || requestsData.length === 0) {
        setPendingRequests([]);
        return;
      }

      // Fetch profiles separately (role_requests.user_id references auth.users, not profiles directly)
      const userIds = [...new Set(requestsData.map((r: any) => r.user_id))];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      if (profilesError) {
        console.warn('Error fetching profiles for role requests:', profilesError);
        // Continue without profile data rather than failing completely
      }

      // Create a map of user_id to profile
      const profilesMap = new Map(
        (profilesData || []).map((p: any) => [p.id, p])
      );

      // Combine requests with profile data
      const requestsWithUser = requestsData.map((req: any) => {
        const profile = profilesMap.get(req.user_id);
        return {
          ...req,
          user_name: profile?.full_name || null,
          user_email: profile?.email || null,
        };
      });

      setPendingRequests(requestsWithUser);
    } catch (error: any) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to fetch role requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRequest = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('role_requests' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (error) throw error;
      setMyRequest(data as any);
    } catch (error: any) {
      console.error('Error fetching my request:', error);
    }
  };

  const submitRoleRequest = async (
    role: AppRole,
    justification: string,
    characterType: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current role
      const { data: currentRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      const currentRole = currentRoles?.[0]?.role || null;

      const { error } = await supabase
        .from('role_requests' as any)
        .insert({
          user_id: user.id,
          requested_role: role,
          current_role: currentRole,
          justification,
          character_type: characterType,
        });

      if (error) throw error;

      toast.success('Role request submitted! Awaiting approval.');
      await fetchMyRequest();
    } catch (error: any) {
      console.error('Error submitting request:', error);
      if (error.code === '23505') {
        toast.error('You already have a pending role request');
      } else {
        toast.error('Failed to submit role request');
      }
      throw error;
    }
  };

  const approveRequest = async (requestId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get request details
      const { data: request, error: fetchError } = await supabase
        .from('role_requests' as any)
        .select('user_id, requested_role')
        .eq('id', requestId)
        .single();

      if (fetchError) throw fetchError;
      const typedRequest = request as any;

      // Update request status
      const { error: updateError } = await supabase
        .from('role_requests' as any)
        .update({
          status: 'approved',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', requestId);

      if (updateError) throw updateError;

      // Assign the new role
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: typedRequest.user_id,
          role: typedRequest.requested_role,
        });

      if (roleError) throw roleError;

      toast.success('Role request approved');
      await fetchPendingRequests();
    } catch (error: any) {
      console.error('Error approving request:', error);
      toast.error('Failed to approve request');
      throw error;
    }
  };

  const rejectRequest = async (requestId: string, reason: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('role_requests' as any)
        .update({
          status: 'rejected',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason,
        })
        .eq('id', requestId);

      if (error) throw error;

      toast.success('Role request rejected');
      await fetchPendingRequests();
    } catch (error: any) {
      console.error('Error rejecting request:', error);
      toast.error('Failed to reject request');
      throw error;
    }
  };

  useEffect(() => {
    fetchPendingRequests();
    fetchMyRequest();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('role_requests_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'role_requests',
        },
        () => {
          fetchPendingRequests();
          fetchMyRequest();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  return {
    pendingRequests,
    myRequest,
    loading,
    submitRoleRequest,
    approveRequest,
    rejectRequest,
    refetch: fetchPendingRequests,
  };
};
